import React, { useContext, useState } from "react";
import { t } from "i18next";
import { AuthContext } from "@/App.jsx";
import useFormState from "../../../utils/useFormState";
import CustomerSelect from "Components/customerSelect/CustomerSelect";
import Button from "Components/button/Button";
import WarningBanner from "Components/warningBanner/WarningBanner";
import QboxmailApi from "../../../qboxmailApi";
import { alertSuccess, alertError } from "../../../utils/alerts";
import { useHistory } from "react-router-dom";
import Dropzone from "react-dropzone";
import { Icon, upload, file } from "Icons/index";
import { convertFileToBase64 } from "Utils/file";
import cx from "classnames";
import s from "./suppressionList.scss";

const acceptedMIMETypes = ["text/plain", "application/vnd.ms-excel", "text/x-csv", "text/csv"];

const New = () => {
  const auth = useContext(AuthContext);
  const [errors, setErrors] = useState([]);

  const {
    formData,
    formErrors,
    setFormFieldValue,
    setFormLoading,
    isFormLoading,
    unsetFormLoading,
    handleFormResponseErrors,
  } = useFormState({
    file: null,
  });

  const canSubmit = !!(formData.file && (auth.isLoggedUserAdmin() ? formData.customer_code : true));

  const history = useHistory();

  const onDrop = async (files, reject) => {
    if (files.length > 1 || reject.lenght > 0) {
      window.alert(t("alert.invalidFileCSVFormat"));
    } else {
      setFormFieldValue({ file: files[0] });
    }
  };

  const formatErrors = (errors) => {
    const fmtd = [];

    for (let line_no in errors) {
      fmtd.push(`Riga ${line_no}: ${Object.values(errors[line_no]).join(", ")}`);
    }

    return fmtd;
  };

  const resetFileField = () => {
    setErrors([]);
    setFormFieldValue({ file: null });
  };

  const submit = async () => {
    if (!canSubmit) return;

    setFormLoading();

    const file = formData.file;

    try {
      const api = new QboxmailApi({ useCookie: true });

      const content = await convertFileToBase64(file);
      const fileMetaData = {
        csv_file_content: content,
        csv_file_name: file.name,
        csv_file_size: file.size,
      };

      delete formData.file;

      try {
        await api.createImportSuppressionList(Object.assign(formData, fileMetaData));
      } catch (error) {
        const err = await error.json();
        console.error(err);

        setErrors(formatErrors(err.errors));
        alertError("Impossibile importare lista di soppressione");
        return;
      }

      alertSuccess(t("delivery.suppressionList.add.alert.success"));

      if (auth.isLoggedUserAdmin()) {
        history.push(`/delivery/suppression-list?customer_code=${formData.customer_code}`);
        return;
      }

      history.push("/delivery/suppression-list");
    } catch (error) {
      console.error(error);
      await handleFormResponseErrors(error);
    } finally {
      unsetFormLoading();
    }
  };

  const renderErrors = () => {
    return (
      <div className={s.errorsLayout}>
        <h3 className="invalidFileLabel">{errors.length} Errori</h3>

        <ul className={s.errorList}>
          {errors.map((e, idx) => (
            <li key={idx}>{e}</li>
          ))}
        </ul>

        <Button className="btnDZ" color="outline" onClick={resetFileField} sizeSmall>
          button.change
        </Button>
      </div>
    );
  };

  // const renderLoading = () => <div className="">Importando : {formData.file.name}...</div>;

  return (
    <div className="contentWrapper">
      <div className="screenContainer flexAlignCenter">
        <div className="mt-4 w-full max-w-[--form-page-width]">
          <div className="sectionTitle titleFP">{t("delivery.suppressionList.add.title")}</div>
        </div>

        <div className="formContainer">
          <div className="flexColumnContainer">
            <div className="infoFP">
              <div className="sectionDesc">{t("suppressionList.imports.add.desc")}</div>
              <div className="wrapBtnFP">
                <Button className="btnDocsFP" docsKey="imports_add" color="docs" sizeSmall>
                  button.docs
                </Button>
                <div className="btnFileFP">
                  <a href={`${BASE_URL_PUBLIC_ASSETS}/Suppression-list-Imports-examples.zip`} download>
                    {t("action.download.example.csv")}
                  </a>
                </div>
              </div>
            </div>

            <div className="formSection">
              {auth.isLoggedUserAdmin() && (
                <CustomerSelect
                  withoutDomainCounter
                  initialValue=""
                  className="inputFull2"
                  label={t("input.label.customer")}
                  onChange={(data) => {
                    setFormFieldValue({ customer_code: data ? data.value : null });
                  }}
                  errors={formErrors.customer_code}
                />
              )}
            </div>

            <div className="formSection">
              {errors.length > 0 ? (
                renderErrors()
              ) : (
                <Dropzone onDrop={onDrop} accept={acceptedMIMETypes}>
                  {(params) => {
                    return (
                      <div
                        {...params.getRootProps()}
                        className={cx(s.dropzone, {
                          [s.reject]: params.isDragReject,
                          [s.accept]: params.isDragAccept || (formData.file && !params.isDragActive),
                        })}
                      >
                        {/* dragging not active */}
                        {!formData.file && !params.isDragActive && (
                          <>
                            <input {...params.getInputProps()} />
                            <Icon className="iconDZ" svg={upload} width="1rem" height="1rem" />
                            <div className="textDZ">{t("input.label.dragHere")}</div>
                          </>
                        )}

                        {/* dragging active but invalid */}
                        {params.isDragReject && (
                          <div className="textDZ">{"Formato file non accettato. Accettati solo file .csv e .txt"}</div>
                        )}

                        {/* dragging active and file valid */}
                        {params.isDragAccept && <div className="textDZ">{"Formato file valido"}</div>}

                        {/* file present */}
                        {formData.file && (
                          <div className={s.acceptedContent}>
                            <div className={s.filePreview}>
                              <Icon className="iconDZ" svg={file} width="1rem" height="1rem" />
                              <div className="textDZ">{formData.file.name}</div>
                            </div>

                            <Button className="btnDZ" color="outline" onClick={resetFileField} sizeSmall>
                              button.change
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Dropzone>
              )}
            </div>
          </div>

          <div className="flexColumnContainer">
            {formErrors.onForm && (
              <div className="formSection">
                <WarningBanner color="red" msg={formErrors.onForm} />
              </div>
            )}
            <div className="formFooter">
              <Button
                color="text"
                type="button"
                onClick={() => {
                  history.push("/delivery/suppression-list");
                }}
              >
                button.cancel
              </Button>
              <Button onClick={submit} disabled={!canSubmit} color="accent" loading={isFormLoading}>
                button.add
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default New;
